import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import type { ReviewPayload } from '@/components/common/ReviewDecision';
import { countsBySubmitterSchema, type CountsBySubmitter } from '@/types/countsBySubmitter.types';
import {
  instituteSubmissionDetailSchema,
  instituteSubmissionSummarySchema,
  paginatedInstituteSubmissionsSchema,
  type InstituteSubmissionCountsBySubmitterParams,
  type InstituteSubmissionDetail,
  type InstituteSubmissionListParams,
  type InstituteSubmissionSummary,
  type InstituteSubmissionSummaryParams,
  type PaginatedInstituteSubmissions,
} from '../types/instituteSubmission.types';

export const instituteSubmissionQueryKeys = {
  all: () => ['institute-submissions'] as const,
  lists: () => [...instituteSubmissionQueryKeys.all(), 'list'] as const,
  list: (params: InstituteSubmissionListParams) =>
    [...instituteSubmissionQueryKeys.lists(), params] as const,
  summaries: () => [...instituteSubmissionQueryKeys.all(), 'summary'] as const,
  summary: (params: InstituteSubmissionSummaryParams) =>
    [...instituteSubmissionQueryKeys.summaries(), params] as const,
  countsBySubmitter: (params: InstituteSubmissionCountsBySubmitterParams) =>
    [...instituteSubmissionQueryKeys.all(), 'counts-by-submitter', params] as const,
  detail: (id: number) => [...instituteSubmissionQueryKeys.all(), 'detail', id] as const,
};

export const instituteSubmissionInfiniteListQueryOptions = (
  params: InstituteSubmissionListParams,
) =>
  infiniteQueryOptions({
    queryKey: instituteSubmissionQueryKeys.list(params),
    queryFn: async ({ pageParam }): Promise<PaginatedInstituteSubmissions> => {
      const res = await apiClient.get(API.instituteSubmission.list, {
        params: { ...params, page: pageParam },
      });
      return paginatedInstituteSubmissionsSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const instituteSubmissionSummaryQueryOptions = (params: InstituteSubmissionSummaryParams) =>
  queryOptions({
    queryKey: instituteSubmissionQueryKeys.summary(params),
    queryFn: async (): Promise<InstituteSubmissionSummary> => {
      const res = await apiClient.get(API.instituteSubmission.summary, { params });
      return instituteSubmissionSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const instituteSubmissionCountsBySubmitterQueryOptions = (
  params: InstituteSubmissionCountsBySubmitterParams,
) =>
  queryOptions({
    queryKey: instituteSubmissionQueryKeys.countsBySubmitter(params),
    queryFn: async (): Promise<CountsBySubmitter> => {
      const res = await apiClient.get(API.instituteSubmission.countsBySubmitter, { params });
      return countsBySubmitterSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function approveInstituteSubmission(
  id: number,
  payload: ReviewPayload,
): Promise<InstituteSubmissionDetail> {
  const res = await apiClient.patch(API.instituteSubmission.approve(id), payload);
  return instituteSubmissionDetailSchema.parse(res.data);
}

export async function rejectInstituteSubmission(
  id: number,
  payload: ReviewPayload,
): Promise<InstituteSubmissionDetail> {
  const res = await apiClient.patch(API.instituteSubmission.reject(id), payload);
  return instituteSubmissionDetailSchema.parse(res.data);
}

export const instituteSubmissionDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: instituteSubmissionQueryKeys.detail(id),
    queryFn: async (): Promise<InstituteSubmissionDetail> => {
      const res = await apiClient.get(API.instituteSubmission.detail(id));
      return instituteSubmissionDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
